import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Mail, Phone, Calendar, DollarSign, ShoppingBag, Star, Ticket, Bell, Activity,
  Download, Ban, CheckCircle, Trash2, Send, TrendingUp, Package, BarChart3
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils-helpers';
import { toast } from 'sonner';

export default function UserDetail360() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/admin/users/${userId}/360`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/users/${userId}/export`, { 
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer_${user.email}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customer data exported successfully');
    } catch (err) {
      toast.error('Failed to export customer data');
    } finally {
      setExporting(false);
    }
  };

  const handleSuspend = async () => {
    try {
      await api.post(`/admin/users/${userId}/suspend`);
      toast.success(user.is_active ? 'Customer suspended' : 'Customer activated');
      window.location.reload();
    } catch (err) {
      toast.error('Failed to update customer status');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Customer deleted');
      navigate('/admin/users');
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const handleSendPromo = async () => {
    if (!promoMessage.trim()) {
      toast.error('Please enter a promo message');
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/send-promo`, { message: promoMessage });
      toast.success('Promo offer sent successfully');
      setPromoDialog(false);
      setPromoMessage('');
    } catch (err) {
      toast.error('Failed to send promo offer');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">User not found</div>;

  const user = data.user;
  const stats = data.statistics;

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      vendor: 'bg-blue-100 text-blue-800',
      delivery_worker: 'bg-green-100 text-green-800',
      end_user: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[role]}>{role.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/users')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" data-font="display">360° Customer View</h1>
            <p className="text-muted-foreground">Complete activity profile for {user.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-customer-button">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
          <Button variant="outline" onClick={() => setPromoDialog(true)} data-testid="send-promo-button">
            <Send className="h-4 w-4 mr-2" />
            Send Promo
          </Button>
          <Button 
            variant={user.is_active === false ? "default" : "outline"} 
            onClick={() => setSuspendDialog(true)}
            data-testid="suspend-customer-button"
          >
            {user.is_active === false ? <CheckCircle className="h-4 w-4 mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
            {user.is_active === false ? 'Activate' : 'Suspend'}
          </Button>
          <Button variant="destructive" onClick={() => setDeleteDialog(true)} data-testid="delete-customer-button">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Profile Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex gap-6">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-3xl font-bold text-primary">{user.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold">{user.name}</h2>
                    {user.user_number && (
                      <Badge 
                        variant="outline" 
                        className="font-mono text-xs border-[hsl(351,96%,31%)] text-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]"
                        data-testid="user-id-badge"
                      >
                        {user.user_number}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4" /> {user.email}
                  </p>
                  {user.phone && (
                    <p className="text-muted-foreground flex items-center gap-2 mt-1">
                      <Phone className="h-4 w-4" /> {user.phone}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {getRoleBadge(user.role)}
                  {user.is_verified && <Badge className="bg-green-100 text-green-800">Verified</Badge>}
                  {user.is_online && <Badge className="bg-blue-100 text-blue-800">● Online</Badge>}
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Joined {formatDate(user.created_at)}
                </p>
              </div>
            </div>

            {/* Enhanced Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-primary">{stats.total_orders || 0}</p>
                <p className="text-xs text-muted-foreground">Total Orders</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-primary">{formatCurrency(stats.total_spent || 0)}</p>
                <p className="text-xs text-muted-foreground">Total Spent</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-primary">{formatCurrency(stats.lifetime_value || 0)}</p>
                <p className="text-xs text-muted-foreground">LTV</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold text-primary">{stats.active_orders || 0}</p>
                <p className="text-xs text-muted-foreground">Active Orders</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Analytics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.avg_order_value || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Repeat Purchase Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.repeat_rate || 0}%</div>
            <p className="text-xs text-muted-foreground mt-1">Customer loyalty</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Favorite Products</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.favorite_products_count || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Most ordered items</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue="orders" className="w-full">
        <TabsList>
          <TabsTrigger value="orders"><ShoppingBag className="h-4 w-4 mr-2" />Orders ({data.orders.length})</TabsTrigger>
          <TabsTrigger value="reviews"><Star className="h-4 w-4 mr-2" />Reviews ({data.reviews.length})</TabsTrigger>
          <TabsTrigger value="tickets"><Ticket className="h-4 w-4 mr-2" />Tickets ({data.tickets.length})</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="h-4 w-4 mr-2" />Notifications</TabsTrigger>
          <TabsTrigger value="activity"><Activity className="h-4 w-4 mr-2" />Activity Log</TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
            </CardHeader>
            <CardContent>
              {data.orders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No orders found</p>
              ) : (
                <div className="space-y-3">
                  {data.orders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                      <div>
                        <p className="font-medium">{order.id}</p>
                        <p className="text-sm text-muted-foreground">{order.vendor_name}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(order.total)}</p>
                        <Badge className={order.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reviews Written</CardTitle>
            </CardHeader>
            <CardContent>
              {data.reviews.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No reviews found</p>
              ) : (
                <div className="space-y-3">
                  {data.reviews.map(review => (
                    <div key={review.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium">{review.vendor_name}</p>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{review.comment}</p>
                      <p className="text-xs text-muted-foreground mt-2">{formatDate(review.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Support Tickets</CardTitle>
            </CardHeader>
            <CardContent>
              {data.tickets.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No tickets found</p>
              ) : (
                <div className="space-y-3">
                  {data.tickets.map(ticket => (
                    <div key={ticket.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{ticket.id}: {ticket.subject}</p>
                          <p className="text-sm text-muted-foreground">{ticket.category}</p>
                        </div>
                        <Badge className={ticket.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {ticket.status}
                        </Badge>
                      </div>
                      <p className="text-sm">{ticket.description}</p>
                      <p className="text-xs text-muted-foreground mt-2">{formatDate(ticket.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.notifications.slice(0, 20).map(notif => (
                  <div key={notif.id} className="flex items-start gap-3 p-3 border-b">
                    <Bell className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{notif.title}</p>
                      <p className="text-sm text-muted-foreground">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(notif.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.activity_logs.map(log => (
                  <div key={log.id} className="flex items-start gap-3 p-3 border-l-2 border-primary/20">
                    <Activity className="h-4 w-4 mt-1 text-primary" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{log.action}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(log.created_at)}</p>
                      {log.details && (
                        <p className="text-xs text-muted-foreground mt-1">{JSON.stringify(log.details).substring(0, 100)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AlertDialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{user.is_active === false ? 'Activate' : 'Suspend'} Customer</AlertDialogTitle>
            <AlertDialogDescription>
              {user.is_active === false 
                ? 'This will reactivate the customer account and restore access.'
                : 'This will suspend the customer account and prevent them from placing orders.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSuspend}>
              {user.is_active === false ? 'Activate' : 'Suspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the customer account and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Promo Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Promo Message</label>
              <Textarea 
                placeholder="Enter your promotional message..."
                value={promoMessage}
                onChange={(e) => setPromoMessage(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoDialog(false)}>Cancel</Button>
            <Button onClick={handleSendPromo}>Send Promo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
